import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useSharedCustomerLocation } from '@/features/location/useCustomerLocation';
import type { PublicStore } from '@/features/menu/types';
import { useCartWorkflowStore } from '@/state/cartWorkflowStore';
import { useCustomerSessionStore } from '@/state/customerSessionStore';
import { browseStoreFromPublic, useBrowseStore } from '@/state/browseStoreStore';
import { CustomerIcon } from './CustomerIcon';
import { usePublicStores } from './discoveryApi';
import { composedStoreAddress, sortStoreRows } from './storeDistance';

function normalizeStoreCode(value?: string | null) {
  return value?.trim().toUpperCase() || undefined;
}

export function StoreSelectorSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stores = usePublicStores();
  const location = useSharedCustomerLocation();
  const selected = useBrowseStore((state) => state.store);
  const selectStore = useBrowseStore((state) => state.selectStore);
  const orderingSession = useCustomerSessionStore((state) => state.orderingSession);
  const orderingStore = useCustomerSessionStore((state) => state.store?.storeCode);
  const clearSession = useCustomerSessionStore((state) => state.clearSession);
  const recovery = useCartWorkflowStore((state) => state.recovery);
  const pendingRecovery = useCartWorkflowStore((state) => state.pendingRecovery);
  const clearRecovery = useCartWorkflowStore((state) => state.clearRecovery);
  const sheetRef = useRef<HTMLElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const rows = useMemo(
    () =>
      sortStoreRows(stores.data ?? [], {
        orderingStoreCode: orderingStore,
        selectedStoreCode: selected?.storeCode,
        customerLocation: location.status === 'granted' ? location.location : undefined,
      }),
    [location.location, location.status, orderingStore, selected?.storeCode, stores.data]
  );

  useEffect(() => {
    if (!open) return undefined;
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusInitialControl = () =>
      sheetRef.current?.querySelector<HTMLElement>('[data-store-sheet-close]')?.focus();
    const frame = window.requestAnimationFrame(focusInitialControl);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !sheetRef.current) return;
      const focusable = Array.from(
        sheetRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
        )
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus();
    };
  }, [onClose, open]);

  if (!open) return null;

  const choose = (store: PublicStore) => {
    const targetStoreCode = normalizeStoreCode(store.storeCode);
    const selectedStoreCode = normalizeStoreCode(selected?.storeCode);
    const activeStoreCodes = [
      normalizeStoreCode(orderingStore),
      normalizeStoreCode(recovery?.storeCode),
      normalizeStoreCode(pendingRecovery?.storeCode),
    ].filter((value): value is string => Boolean(value));
    const hasActiveOrderingState = Boolean(orderingSession || recovery || pendingRecovery);
    const changesActiveStore = activeStoreCodes.some((storeCode) => storeCode !== targetStoreCode);
    // A restored session has no Store metadata until it has been revalidated. Treat a change
    // conservatively: the capability is discarded only after an explicit confirmation.
    const mustDiscardActiveState =
      selectedStoreCode !== targetStoreCode &&
      hasActiveOrderingState &&
      (changesActiveStore || activeStoreCodes.length === 0);

    if (
      mustDiscardActiveState &&
      !window.confirm(
        'Đổi cửa hàng sẽ xóa phiên gọi món và giỏ hàng chưa đặt tại cửa hàng trước. Giỏ hàng đó không thể chuyển sang cửa hàng mới. Bạn có muốn tiếp tục?'
      )
    )
      return;

    if (mustDiscardActiveState) {
      clearSession();
      clearRecovery();
      queryClient.removeQueries({ queryKey: ['public-cart'] });
      queryClient.removeQueries({ queryKey: ['public-cart-promotions'] });
      void queryClient.invalidateQueries();
    }

    selectStore(browseStoreFromPublic(store), 'manual');
    onClose();

    // An ordering menu/cart route is scoped to the removed capability. The landing page creates
    // Store-scoped discovery queries again for the newly selected Store.
    if (mustDiscardActiveState) navigate('/', { replace: true });
  };

  const canRequestLocation =
    location.status === 'idle' ||
    location.status === 'denied' ||
    location.status === 'unavailable' ||
    location.status === 'timeout';
  const locationStatus =
    location.status === 'requesting'
      ? 'Đang xác định vị trí của bạn…'
      : location.status === 'denied'
        ? 'Bạn đã từ chối vị trí. Bạn vẫn có thể chọn cửa hàng thủ công.'
        : location.status === 'unsupported'
          ? 'Trình duyệt này không hỗ trợ vị trí.'
          : location.status === 'unavailable' || location.status === 'timeout'
            ? 'Chưa thể lấy vị trí. Bạn vẫn có thể chọn cửa hàng thủ công.'
            : undefined;
  const currentLocation =
    location.status === 'granted' && location.location ? location.location : undefined;

  return (
    <div className="store-sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        ref={sheetRef}
        className="store-selector-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="store-selector-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-heading">
          <div>
            <p id="store-selector-title">Cửa hàng</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Đóng" data-store-sheet-close>
            ×
          </button>
        </div>
        <div className="store-location-controls" aria-live="polite">
          {currentLocation && (
            <div className="customer-current-location">
              <CustomerIcon name="pin" />
              <span>
                <strong>Vị trí hiện tại của thiết bị</strong>
                <small>
                  {currentLocation.latitude.toFixed(5)}, {currentLocation.longitude.toFixed(5)}
                </small>
                <small>Độ chính xác khoảng {Math.round(currentLocation.accuracy)} m</small>
              </span>
              <div className="customer-current-location-actions">
                <button type="button" onClick={location.refreshLocation}>
                  Cập nhật vị trí
                </button>
                <button type="button" onClick={location.resetLocation}>
                  Không dùng vị trí
                </button>
              </div>
            </div>
          )}
          {canRequestLocation && (
            <button
              type="button"
              className="store-location-button"
              onClick={location.requestLocation}
            >
              <CustomerIcon name="pin" /> Dùng vị trí của tôi
            </button>
          )}
          {locationStatus && <p role="status">{locationStatus}</p>}
        </div>
        {stores.isPending ? (
          <p role="status">Đang tải cửa hàng…</p>
        ) : stores.isError ? (
          <button type="button" onClick={() => void stores.refetch()}>
            Thử lại
          </button>
        ) : rows.length === 0 ? (
          <p role="status">Chưa có cửa hàng để chọn.</p>
        ) : (
          <ul className="store-selector-list" aria-label="Danh sách cửa hàng">
            {rows.map((store) => {
              const isSelected =
                normalizeStoreCode(selected?.storeCode) === normalizeStoreCode(store.storeCode);
              const isOrdering = normalizeStoreCode(orderingStore) === normalizeStoreCode(store.storeCode);
              const address = composedStoreAddress(store);
              return (
                <li key={store.storeCode}>
                  <button
                    type="button"
                    className={isSelected ? 'is-selected' : ''}
                    onClick={() => choose(store)}
                    aria-pressed={isSelected}
                  >
                    <CustomerIcon name="pin" />
                    <span>
                      <strong>{store.name ?? store.storeCode}</strong>
                      {address && <small>{address}</small>}
                      {store.distanceKm !== undefined ? (
                        <small data-testid={`store-distance-${store.storeCode}`}>
                          Khoảng{' '}
                          {store.distanceKm < 1
                            ? `${Math.round(store.distanceKm * 1_000)} m`
                            : `${store.distanceKm.toFixed(1)} km`}
                        </small>
                      ) : (
                        <small>Chưa có tọa độ cửa hàng để ước tính khoảng cách.</small>
                      )}
                    </span>
                    <span className="store-row-status">
                      <small>{store.acceptingOrders ? 'Đang nhận đơn' : 'Tạm đóng'}</small>
                      {isOrdering && <small>Phiên QR</small>}
                      {isSelected && <small>Đã chọn</small>}
                    </span>
                    <CustomerIcon name="chevron" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
